import {Component, Input, OnChanges, ChangeDetectionStrategy} from '@angular/core';

import {PartnerStat} from '../../../../../models/statistic/partner-stat';
import {Partner} from '../../../../../models/partner/partner';
import {MoneyPipe} from '../../../../../pipes/money/money.pipe';
import {BaseChartDirective} from 'ng2-charts';
import {ChartOptions, ChartType} from 'chart.js';
import {DateStat} from '../../../../../models/statistic/date-stat';

@Component({
  selector: 'app-history-performance',
  imports: [
    MoneyPipe,
    BaseChartDirective
],
  templateUrl: './history-performance.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './history-performance.component.scss'
})
export class HistoryPerformanceComponent implements OnChanges {

  @Input() partner: Partner | null = null;
  @Input() partnerStat: PartnerStat = new PartnerStat();
  @Input() loaded: boolean = false;

  lineType: ChartType = 'line';
  barType: ChartType = 'bar';
  lineOptions: ChartOptions = {
    responsive: true,
    plugins: {
      legend: { position: 'top' },
      tooltip: { enabled: true },
    }
  };
  barOptions: ChartOptions = {
    responsive: true,
    plugins: {
      legend: { display: false },
      tooltip: { enabled: true }
    },
    scales: {
      x: { grid: { display: false } },
      y: { beginAtZero: true }
    }
  };
  last12MonthsChart = {
    labels: [] as string[],
    datasets: [{
      label: 'Incomes',
      data: [200, 180, 220, 240, 260, 300, 400, 380, 360, 340, 300, 280],
      borderColor: '#27ae60',
      backgroundColor: 'rgba(39, 174, 96, 0.2)',
      fill: true
    }, {
      label: 'Costs',
      data: [100, 120, 140, 160, 180, 200, 220, 240, 260, 280, 300, 320],
      borderColor: '#c0392b',
      backgroundColor: 'rgba(192, 57, 43, 0.2)',
      fill: true
    }]
  };
  last30daysChart = {
    labels: [] as string[],
    datasets: [{
      label: 'Daily Revenue',
      data: [10, 20, 15, 25, 30, 40, 35, 50, 45, 60, 70, 80, 90, 100, 95, 85, 75, 65, 55, 45, 50, 60, 70, 80, 85, 90, 95, 100, 110, 120],
      backgroundColor: '#5D9CEC',
      borderColor: '#3A78C3',
      borderWidth: 1
    }]
  };

  ngOnChanges(): void {
    if (!this.loaded) {
      return;
    }

    const allMonths = this.getLast12Months();
    const formattedLabels = allMonths.map(this.formatMonthYear);
    const { revenue, cost } = this.mapMonthData(allMonths, this.partnerStat.history.last12Months);

    this.last12MonthsChart.labels = formattedLabels;
    this.last12MonthsChart.datasets[0].data = revenue;
    this.last12MonthsChart.datasets[1].data = cost;

    const allDays = this.getLast30Days();
    const { dailyRevenue } = this.mapDayData(allDays, this.partnerStat.history.last30days);

    this.last30daysChart.labels = allDays.map(this.formatMonthDay);
    this.last30daysChart.datasets[0].data = dailyRevenue;
  }

  private getLast12Months(): string[] {
    const result: string[] = [];
    const date = new Date();
    for (let i = 11; i >= 0; i--) {
      const d = new Date(date.getFullYear(), date.getMonth() - i);
      result.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
    }
    return result;
  }

  private mapMonthData(allKeys: string[], items: DateStat[]) {
    const itemMap = new Map(items.map(i => [i.key, i]));

    const revenue: number[] = [];
    const cost: number[] = [];

    for (const key of allKeys) {
      const item = itemMap.get(key);
      revenue.push((item && item.revenue) ? item.revenue / 100 : 0);
      cost.push((item && item.cost) ? item.cost / 100 : 0);
    }

    return { revenue, cost };
  }

  private getLast30Days(): string[] {
    const result: string[] = [];
    const today = new Date();
    for (let i = 29; i >= 0; i--) {
      const d = new Date(today);
      d.setDate(today.getDate() - i);
      result.push(d.toISOString().slice(0, 10));
    }

    return result;
  }

  private mapDayData(allKeys: string[], items: DateStat[]) {
    const itemMap = new Map(items.map(i => [i.key, i]));
    const dailyRevenue: number[] = [];
    for (const key of allKeys) {
      const item = itemMap.get(key);
      dailyRevenue.push((item && item.revenue) ? item.revenue / 100 : 0);
    }

    return { dailyRevenue };
  }

  private formatMonthYear(key: string): string {
    const [year, month] = key.split('-').map(Number);
    const date = new Date(year, month - 1);

    return new Intl.DateTimeFormat('en-US', { month: 'short', year: 'numeric' }).format(date);
  }

  private formatMonthDay(key: string): string {
    const [year, month, day] = key.split('-').map(Number);
    const date = new Date(year, month - 1, day);

    return new Intl.DateTimeFormat('en-US', { day: 'numeric', month: 'short' }).format(date);
  }
}
